import { memo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ensureInningScores, isGameOver } from "./utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InningBox({ index, state, inningScores, gameId, onUpdate, editingCell, setEditingCell, editValue, setEditValue }) {
  const innNum = index + 1;
  const isCurrent = innNum === state.inning;
  const innScore = inningScores[index] || { top: null, bottom: null };
  const isEditingTop = editingCell?.index === index && editingCell?.half === 'top';
  const isEditingBottom = editingCell?.index === index && editingCell?.half === 'bottom';
  const isPastTop = innNum < state.inning || (innNum === state.inning && state.half === 'bottom');
  const isPastBottom = innNum < state.inning;
  const topDisplay = innScore.top !== null ? innScore.top : (isPastTop ? 0 : '');
  const bottomDisplay = innScore.bottom !== null ? innScore.bottom : (isPastBottom ? 0 : '');

  const handleCellClick = (half) => {
    const currentValue = inningScores[index]?.[half];
    setEditValue(currentValue !== null && currentValue !== undefined ? String(currentValue) : "");
    setEditingCell({ index, half });
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^\d{0,2}$/.test(val)) setEditValue(val);
  };

  const handleInputBlur = async () => {
    if (editingCell) {
      const numValue = editValue === "" ? null : parseInt(editValue, 10);
      const newInningScores = [...inningScores];
      while (newInningScores.length < editingCell.index + 1) newInningScores.push({ top: null, bottom: null });
      newInningScores[editingCell.index] = { ...newInningScores[editingCell.index], [editingCell.half]: numValue };
      const awayTotal = newInningScores.reduce((sum, inn) => sum + (inn.top || 0), 0);
      const homeTotal = newInningScores.reduce((sum, inn) => sum + (inn.bottom || 0), 0);
      try {
        await axios.put(`${API}/games/${gameId}/state`, { ...state, inning_scores: newInningScores, away_score: awayTotal, home_score: homeTotal });
        onUpdate?.();
      } catch { toast.error("Failed to save score edit"); }
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleInputBlur();
    else if (e.key === 'Escape') setEditingCell(null);
  };

  return (
    <div className="flex flex-col items-center">
      <span className={`text-[9px] font-black ${isCurrent ? 'text-amber-400' : 'text-slate-600'}`}>{innNum}</span>
      <div className={`relative w-7 h-7 rounded overflow-hidden ${isCurrent ? 'ring-1 ring-amber-500/60' : ''}`}>
        <div className={`absolute inset-0 cursor-pointer transition-colors ${isCurrent && state.half === 'top' ? 'bg-amber-500/80' : 'bg-slate-800 hover:bg-slate-700'}`}
          style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }} onClick={() => handleCellClick('top')} />
        <div className={`absolute inset-0 cursor-pointer transition-colors ${isCurrent && state.half === 'bottom' ? 'bg-amber-500/80' : 'bg-slate-900 hover:bg-slate-800'}`}
          style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }} onClick={() => handleCellClick('bottom')} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom right, transparent calc(50% - 0.5px), rgba(0,0,0,0.5) calc(50% - 0.5px), rgba(0,0,0,0.5) calc(50% + 0.5px), transparent calc(50% + 0.5px))' }} />
        {isEditingTop ? (
          <input type="text" value={editValue} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleKeyDown} autoFocus className="absolute top-0 left-0.5 w-4 h-4 text-center text-[10px] font-black bg-white text-black rounded-sm outline-none z-10" />
        ) : (
          <span className={`absolute top-0 left-1 text-[10px] font-black pointer-events-none ${isCurrent && state.half === 'top' ? 'text-black' : 'text-slate-400'}`}>{topDisplay}</span>
        )}
        {isEditingBottom ? (
          <input type="text" value={editValue} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleKeyDown} autoFocus className="absolute bottom-0 right-0.5 w-4 h-4 text-center text-[10px] font-black bg-white text-black rounded-sm outline-none z-10" />
        ) : (
          <span className={`absolute bottom-0 right-1 text-[10px] font-black pointer-events-none ${isCurrent && state.half === 'bottom' ? 'text-black' : 'text-slate-400'}`}>{bottomDisplay}</span>
        )}
      </div>
    </div>
  );
}

function EditableTeamName({ name, gameId, teamType, onUpdate, isActive }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(name);

  const save = async () => {
    setEditing(false);
    if (editVal.trim() && editVal.trim() !== name) {
      try {
        await axios.put(`${API}/games/${gameId}/teams/${teamType}/name`, { name: editVal.trim() });
        onUpdate();
      } catch { toast.error("Failed to rename"); setEditVal(name); }
    } else { setEditVal(name); }
  };

  if (editing) {
    return (
      <input type="text" value={editVal} onChange={(e) => setEditVal(e.target.value)}
        onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setEditVal(name); } }}
        autoFocus className="w-24 text-[11px] font-bold bg-slate-800 border border-amber-500/50 rounded px-1 py-0.5 text-white outline-none" />
    );
  }

  const display = name.length > 14 ? name.slice(0, 14) + '...' : name;
  return (
    <span onDoubleClick={() => { setEditing(true); setEditVal(name); }}
      className={`text-[11px] font-bold tracking-wide flex-shrink-0 cursor-default select-none ${isActive ? 'text-amber-400' : 'text-slate-500'}`}
      title={`${name} (double-click to edit)`}>{display}</span>
  );
}

function Scoreboard({ game, gameId, onUpdate }) {
  const navigate = useNavigate();
  const state = game.state;
  const baseInnings = game.innings_total || 9;
  // After the final out, server's flip_half_inning still bumps inning + half ("top of N+1").
  // That phantom half-inning is never played, so we don't want to render an empty column
  // for it. If the game is over and we're at the start of a fresh top half, the actual last
  // played inning is N-1. Walk-offs already end on bottom-of-N so they need no adjustment.
  const isOver = !!isGameOver(state, baseInnings);
  const lastPlayedInning = (isOver && state.half === "top" && state.inning > 1) ? state.inning - 1 : state.inning;
  const displayedInnings = Math.max(baseInnings, lastPlayedInning);
  const inningScores = ensureInningScores(state.inning_scores, displayedInnings);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const weather = game.weather;


  return (
    <nav data-testid="scoreboard" className="flex-shrink-0 bg-slate-900 border-b border-slate-800 px-2 py-1">
      <div className="flex items-center gap-2">
        {/* Scoreboard Core */}
        <div className="flex-1 flex items-center justify-center gap-1.5 min-w-0">
          {/* Away */}
          <EditableTeamName name={game.away_team.name} gameId={gameId} teamType="away" onUpdate={onUpdate} isActive={state.half === 'top'} />
          <span className="score-text text-base font-black flex-shrink-0">{state.away_score}</span>

          {/* Innings — hidden on mobile */}
          <div className="hidden md:flex items-center gap-px mx-1 flex-shrink-0">
            {Array.from({ length: displayedInnings }, (_, i) => (
              <InningBox key={i} index={i} state={state} inningScores={inningScores} gameId={gameId} onUpdate={onUpdate} editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />
            ))}
          </div>

          {/* Home */}
          <span className="score-text text-base font-black flex-shrink-0">{state.home_score}</span>
          <EditableTeamName name={game.home_team.name} gameId={gameId} teamType="home" onUpdate={onUpdate} isActive={state.half === 'bottom'} />

          {/* Divider */}
          <div className="w-px h-5 bg-slate-700 mx-1.5 flex-shrink-0" />

          {/* Count: B S O, as dot columns. The numeric "0-0/1" readout that used to
              sit alongside was removed as redundant — the dots are the display.
              The data-display-* attributes remain and are the machine-readable
              path (a screenshot cannot be parsed, and these can). They are
              namespaced on purpose: this component renders `displayGame`, which
              intentionally lags the server by up to ~6s during a resolution
              animation. Read window.__JUNKBALL_STATE__.server for authoritative
              values. */}
          <div
            className="flex items-center gap-2 flex-shrink-0"
            data-testid="count"
            data-display-balls={state.balls}
            data-display-strikes={state.strikes}
            data-display-outs={state.outs}
            aria-label={`Count ${state.balls} and ${state.strikes}, ${state.outs} out`}
          >
            <div className="flex items-center gap-0.5">
              <span className="text-[8px] font-bold text-emerald-500">B</span>
              {[0,1,2,3].map(i => (
                <div key={i}
                  className={`w-2 h-2 rounded-full ${i < state.balls ? 'bg-emerald-500 shadow-sm shadow-emerald-500/50' : 'bg-slate-700'}`} />
              ))}
            </div>
            <div className="flex items-center gap-0.5">
              <span className="text-[8px] font-bold text-amber-400">S</span>
              {[0,1,2].map(i => (
                <div key={i}
                  className={`w-2 h-2 rounded-full ${i < state.strikes ? 'bg-amber-400 shadow-sm shadow-amber-400/50' : 'bg-slate-700'}`} />
              ))}
            </div>
            <div className="flex items-center gap-0.5">
              <span className="text-[8px] font-bold text-red-500">O</span>
              {[0,1,2].map(i => (
                <div key={i}
                  className={`w-2 h-2 rounded-full ${i < state.outs ? 'bg-red-500 shadow-sm shadow-red-500/50' : 'bg-slate-700'}`} />
              ))}
            </div>
          </div>

          {/* Inning indicator — shows the half/inning currently being played. Once the
              game is over the half-arrow is dimmed (no active half) and the number
              clamps to the last-played inning so we don't display "▲ 10" for a game
              that ended in the bottom of 9. */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <div className="flex flex-col items-center leading-none">
              <span className={`text-[7px] ${!isOver && state.half === 'top' ? 'text-amber-400' : 'text-slate-700'}`}>&#9650;</span>
              <span className={`text-[7px] ${!isOver && state.half === 'bottom' ? 'text-amber-400' : 'text-slate-700'}`}>&#9660;</span>
            </div>
            <span className="text-[11px] font-mono font-bold text-white">{lastPlayedInning}</span>
          </div>
        </div>

        {/* End */}
        <button data-testid="exit-game" onClick={() => navigate("/")} className="text-[11px] text-red-400 hover:text-red-300 transition-colors flex-shrink-0">End</button>
      </div>
    </nav>
  );
}

// Wrapped in memo so the scoreboard doesn't re-render on unrelated GameBoard renders
// (e.g., stats updates, hand-state changes). Re-renders only when game / gameId / onUpdate
// reference change. Pass stable references for onUpdate (useCallback in GameBoard).
export default memo(Scoreboard);
